import React from 'react';
import { Body7, Container } from '@ui';
import { FlatButton, SkeletonButton } from '@ui/buttons';
import { previewDocuments, previewScore } from '@services/scoreSpecs';
import { SpecEditorContext } from './SpecEditorContext';
import { matchCount } from './jsonPath';
import { PreviewScopeControls } from './PreviewScopeControls';
import { PreviewRunHeader } from './PreviewRunHeader';
import { PreviewResultCard } from './PreviewResultCard';
import { formatCount, formatWindow } from './format';
import {
  MAX_PREVIEW_UNITS,
  buildPreviewBody,
  buildResolveBody,
  defaultSelection,
  selectionProblem,
} from './previewSelection';
import { Consequence, MicroLabel, PassiveCard, Row } from './styles';

// Run the prompt against real companies and read what came back, without leaving the editor.
//
// Since fapi #492 this is no longer "hand the endpoint a document id". The endpoint now resolves
// the spec's OWN trigger over a universe and a window and scores whatever that selects — the same
// selection a live or backfill run makes, minus the writes. Three things this has to say out loud:
//
//   * It runs the SAVED row, not what is on screen. The endpoint compiles the stored spec_json.
//   * It spends real money on a real model — up to 25 company-units in one press — and persists
//     nothing. The response carries no cost figure, so the dock cannot tell you what it cost.
//   * It DOES exercise the trigger. The old copy here said the opposite, and that sentence was the
//     single most load-bearing caveat in this panel; it is gone because it is no longer true.
//
// The flow is two steps on purpose. `preview_documents` answers "what does this select" for free
// and reaches the strict compiler, so a mis-set universe, a spec that will not compile and a
// trigger kind that cannot be previewed all surface BEFORE any spend. The paid button stays
// disabled until that has come back with something. It is an explicit button rather than a
// debounced effect because this call materializes the whole candidate list server-side — one
// keystroke, one corpus-wide materialization would be the wrong trade.
//
// The last run lives in SpecEditorContext, not here: this component unmounts when the panel is
// collapsed or flipped to the JSON pane, and 25 paid model calls must not be destroyed by a stray
// click. Note the limit of that: cancelRef only suppresses a setState — `sendApiRequest` threads no
// abort signal, and aborting would not stop the server's spend anyway.

const RUN_HINT_ID = 'preview-run-blocker';

export const PreviewDock = ({ specId, isDirty, isCreate }) => {
  const { definition, previewRun, setPreviewRun, savedEpoch } = React.useContext(SpecEditorContext);

  const [selection, setSelection] = React.useState(defaultSelection);
  const [resolved, setResolved] = React.useState(null);
  const [isResolving, setIsResolving] = React.useState(false);
  const [resolveError, setResolveError] = React.useState(null);
  const [isRunning, setIsRunning] = React.useState(false);
  const [runError, setRunError] = React.useState(null);

  // Read through a ref inside onRun: the closure captures the epoch as it was when the run started,
  // and this is how it sees the current one after the await.
  const savedEpochRef = React.useRef(savedEpoch);
  savedEpochRef.current = savedEpoch;

  const cancelRef = React.useRef({ isCanceled: false });
  React.useEffect(() => {
    const token = cancelRef.current;
    return () => {
      token.isCanceled = true;
    };
  }, []);

  // Bumped on every edit. Clearing `resolved` is not enough on its own: a resolve already in flight
  // knows nothing about the edit, and its answer would land describing a selection the operator has
  // since replaced — arming a paid button for a body whose free pre-flight never ran.
  const selectionSeq = React.useRef(0);

  const onSelectionChange = (patch) => {
    selectionSeq.current += 1;
    setSelection((prev) => ({ ...prev, ...patch }));
    setResolved(null);
    setResolveError(null);
  };

  const problem = selectionProblem(selection);

  const onResolve = async () => {
    if (problem || isResolving || isRunning) return;
    const seq = selectionSeq.current;
    setIsResolving(true);
    setResolveError(null);
    const { preview, error } = await previewDocuments(specId, buildResolveBody(selection));
    if (cancelRef.current.isCanceled) return;
    // Ahead of the staleness bail on purpose: leaving this true would strand the panel on
    // 'Checking…' with its own button disabled and no second resolve able to start.
    setIsResolving(false);
    if (seq !== selectionSeq.current) return;
    if (preview) setResolved(preview);
    else setResolveError(error);
  };

  // `preview_documents` is forwarded verbatim by the service, so `count` is whatever the engine
  // sent — including nothing at all. Absent is its own state: it must not fall through the
  // > 0 / === 0 pair into a silently blocked button with no sentence attached.
  const resolvedCount = resolved && typeof resolved.count === 'number' ? resolved.count : null;

  const canRun = !problem && resolvedCount !== null && resolvedCount > 0 && !isResolving && !isRunning;

  const runBlocker = () => {
    if (isRunning) return 'A run is already in flight.';
    if (problem) return problem;
    if (isResolving) return 'Waiting for the count…';
    if (!resolved) return 'Check what this selects first — a run costs money and this does not.';
    if (resolvedCount === null) return 'The engine did not report how much this selects, so the run stays blocked.';
    if (resolvedCount === 0) return 'This selection resolves to nothing, so there is nothing to run on.';
    return 'This selection cannot be run right now.';
  };

  const onRun = async () => {
    if (!canRun) return;
    setIsRunning(true);
    setRunError(null);
    // Frozen BEFORE the await so the header describes the run that happened, not whatever the
    // controls say by the time it lands.
    const body = buildPreviewBody(selection);
    // The saved row this run is about to compile. If it is replaced while the call is out — someone
    // saved, or the editor reloaded — the results describe a spec that no longer exists, and the
    // provider has already cleared the panel for that reason. Committing them anyway would put the
    // previous version's results back on screen under the new version's heading.
    const epoch = savedEpoch;
    const { run, error } = await previewScore(specId, body);
    const rowReplaced = epoch !== savedEpochRef.current;
    // Committed BEFORE the cancel check, and deliberately. `setPreviewRun` belongs to
    // SpecEditorContext, whose provider sits above the question panels (SpecEditor.js:437) and is
    // still mounted when this dock is not — so a run that lands after the panel was collapsed can
    // still be kept. Bailing here instead would throw away up to 25 model calls of real spend,
    // which is the exact loss the state lift exists to prevent.
    if (run && !rowReplaced) setPreviewRun({ ...run, askedWindow: { from: body.from, to: body.to } });
    // The rest is component-local state, and setting it after unmount is what the token is for.
    if (cancelRef.current.isCanceled) return;
    setIsRunning(false);
    if (!run) setRunError(error);
  };

  if (isCreate) {
    return (
      <PassiveCard>
        <MicroLabel>Run it on real companies</MicroLabel>
        <Container top={6}>
          <Body7 isLowContrast>
            Save this draft first — a preview run compiles the stored spec, not the buffer on screen.
          </Body7>
        </Container>
      </PassiveCard>
    );
  }

  const output = (definition || {}).output_extraction || {};
  const schema = output.output_schema && typeof output.output_schema === 'object' ? output.output_schema : null;
  const bufferModel = ((definition || {}).score_gen || {}).model;
  const declaredPaths = []
    .concat(output.score_jsonpath ? [{ label: 'score', expr: output.score_jsonpath, exactlyOne: true }] : [])
    .concat(
      Array.isArray(output.scores)
        ? output.scores.map((s) => ({ label: (s && s.name) || 'unnamed', expr: s && s.jsonpath, exactlyOne: true }))
        : []
    )
    .concat(
      output.extra_columns && typeof output.extra_columns === 'object'
        ? Object.keys(output.extra_columns).map((name) => ({
            label: `${name} (extra)`,
            expr: output.extra_columns[name],
            exactlyOne: false,
          }))
        : []
    )
    // Per-score extras are checked against the same schema — the scope decides which feed carries
    // the column, not which shape it is extracted from.
    .concat(
      (Array.isArray(output.scores) ? output.scores : []).reduce((acc, score) => {
        const cols = score && typeof score.extra_columns === 'object' && score.extra_columns ? score.extra_columns : {};
        return acc.concat(
          Object.keys(cols).map((name) => ({
            label: `${name} (extra, ${(score && score.name) || 'unnamed'} only)`,
            expr: cols[name],
            exactlyOne: false,
          }))
        );
      }, [])
    );

  return (
    <PassiveCard>
      <MicroLabel>Run it on real companies</MicroLabel>

      <Container top={8}>
        <Consequence $tone="warn">
          {/* The trigger half is proved by the request shape — the engine selects, not the operator.
              The document-set half is NOT claimed here: whether a preview exercises retrieval the
              way a live run does is still unconfirmed with fapi, and question 3 on this same page
              carries a warning that says it does not. Two contradictory sentences on one screen are
              worse than one cautious one. */}
          This runs the SAVED spec, not what is on screen{isDirty ? ' — and there are unsaved edits right now' : ''}. It
          resolves the trigger over the universe and window below rather than over documents you name, spends real money
          on a real model, and writes nothing. At most {MAX_PREVIEW_UNITS} company-units are scored, however many the
          selection resolves to.
        </Consequence>
      </Container>

      <Container top={16}>
        <PreviewScopeControls selection={selection} onChange={onSelectionChange} disabled={isRunning} />
      </Container>

      <Container top={16}>
        <Row $align="flex-start">
          <SkeletonButton
            onClick={onResolve}
            disabled={!!problem || isResolving || isRunning}
            buttonProps={{ 'aria-disabled': !!problem || isResolving || isRunning || undefined }}
          >
            {isResolving ? 'Checking…' : 'Check what this selects'}
          </SkeletonButton>
          {/* Through `buttonProps`, never as bare props: FlatButton destructures only
              children/onClick/disabled/fullWidth and drops everything else, so an `aria-disabled`
              written directly on it reaches no element at all. */}
          <FlatButton
            onClick={onRun}
            buttonProps={{ 'aria-disabled': !canRun, 'aria-describedby': !canRun ? RUN_HINT_ID : undefined }}
          >
            {isRunning ? 'Running…' : 'Run the saved version'}
          </FlatButton>
        </Row>
        {!canRun && runBlocker() && (
          <Container top={8}>
            <Body7 id={RUN_HINT_ID} isLowContrast>
              {runBlocker()}
            </Body7>
          </Container>
        )}
      </Container>

      {/* The free half. Its own live region, because the number here gates a button that spends
          money and it changes without the operator having pressed anything on this line. */}
      <div role="status">
        {resolveError && (
          <Container top={16}>
            <div role="alert">
              <Consequence $tone="bad">{resolveError}</Consequence>
            </div>
          </Container>
        )}

        {resolved && (
          <Container top={16}>
            {resolvedCount === null ? (
              <Body7>
                The engine did not report how many company-units this selects, so there is no way to tell what a run
                would cost. The run stays blocked.
              </Body7>
            ) : resolvedCount > 0 ? (
              <Body7>
                {`This selection resolves to ${formatCount(resolvedCount)} company-unit${
                  resolvedCount === 1 ? '' : 's'
                }.`}
                {resolved.truncated ? ` A run scores at most ${MAX_PREVIEW_UNITS} of them.` : ''}
              </Body7>
            ) : (
              <Body7>
                Nothing matches this trigger, universe and window. A run would score nothing and spend nothing — widen
                the window, or name different companies.
              </Body7>
            )}
            {/* The candidate rows themselves are deliberately not listed: their shape differs by
                trigger kind (a document-available occasion carries a doc_id, a pre-earnings one
                carries a release date and no document at all), and this endpoint has never been
                called in production, so the count and the echoed window are the only two things
                this panel can state without guessing. */}
            {(resolved.resolved_from || resolved.resolved_to) && (
              <Container top={6}>
                <Body7 isLowContrast>{`Resolved over ${formatWindow(
                  resolved.resolved_from,
                  resolved.resolved_to
                )}.`}</Body7>
              </Container>
            )}
            <Container top={6}>
              <Body7 isLowContrast>Checking what this selects costs nothing. Running it calls the model.</Body7>
            </Container>
          </Container>
        )}
      </div>

      {runError && (
        <Container top={16}>
          <div role="alert">
            <Consequence $tone="bad">{runError}</Consequence>
          </div>
        </Container>
      )}

      {previewRun && (
        <Container top={16}>
          <PreviewRunHeader run={previewRun} askedWindow={previewRun.askedWindow} bufferModel={bufferModel} />

          {previewRun.results.length === 0 ? (
            <Container top={8}>
              <Body7 isLowContrast>
                The run returned no results. Nothing in this window fired the trigger, so no model call was made and
                nothing was spent.
              </Body7>
            </Container>
          ) : (
            // Outside any live region on purpose: twenty-five cards read aloud is not an
            // announcement, it is a wall. The envelope above is the announcement.
            previewRun.results.map((result) => (
              <Container key={result.key} top={16}>
                <PreviewResultCard result={result} />
              </Container>
            ))
          )}
        </Container>
      )}

      {declaredPaths.length > 0 && (
        <Container top={16}>
          <MicroLabel>Declared paths against the declared shape</MicroLabel>
          <Container top={6}>
            <Body7 isLowContrast>
              Checked against the buffer on screen, not against the run above — and once, not per company. It was
              previously printed inside every result, which implied a per-row verdict it never was.
            </Body7>
          </Container>
          <Container top={6}>
            {declaredPaths.map((entry) => {
              if (!entry.expr) return null;
              const { supported, matches } = matchCount(entry.expr, schema);
              let verdict;
              if (!schema) verdict = 'no output_schema to check against';
              else if (!supported)
                // The client's resolve() returns 0 or 1 nodes, so a multi-match is only possible for
                // the path forms it abstains on — and only an extra column survives one (a score
                // path matching >1 is extraction_no_match).
                verdict = entry.exactlyOne
                  ? 'outside the path forms this editor evaluates'
                  : 'outside the path forms this editor evaluates — if it matches more than one node the value is stored as a list';
              else if (entry.exactlyOne)
                verdict = matches === 1 ? 'exactly 1 node' : `${matches} nodes — must be exactly 1`;
              else verdict = matches >= 1 ? 'resolves' : '0 nodes — always null';
              return (
                <Body7 key={`${entry.label}:${entry.expr}`} isLowContrast>
                  {`${entry.label}: ${entry.expr} — ${verdict}`}
                </Body7>
              );
            })}
          </Container>
        </Container>
      )}

      <Container top={8}>
        <SkeletonButton
          onClick={() => previewRun && setPreviewRun(null)}
          disabled={!previewRun}
          buttonProps={{ 'aria-disabled': !previewRun || undefined }}
        >
          Clear the last run
        </SkeletonButton>
      </Container>
    </PassiveCard>
  );
};
